import type { CoachExchange, CoachSource } from '../types/coach';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  Animated,
  ViewStyle,
  AccessibilityInfo,
} from 'react-native';
import { NavigationProp } from '../navigation/types';
import { colors, spacing, borderRadius, fontSize } from '../styles/theme';
import { errorMessage } from '../utils/errorMessage';

const MAX_MESSAGE_LENGTH = 500;

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  fadeAnim?: Animated.Value;
  slideAnim?: Animated.Value;
  isError?: boolean;
  retryInput?: string;
  sources?: CoachSource[];
}

/** The words around a chat, so one chat screen can be about you, a result, or a person. */
export interface ChatCopy {
  title: string;
  subtitle: string;
  welcome: string;
  description: string;
  placeholder: string;
  suggestions: [label: string, question: string][];
}

export const askCopy: ChatCopy = {
  title: 'Ask',
  subtitle: 'A conversation about you.',
  welcome: 'A little curiosity about yourself.',
  description: 'Your patterns. Your people. Your occasional plot twist.',
  placeholder: 'Ask anything about yourself…',
  suggestions: [
    ['My patterns', 'What patterns do you notice in how I connect with people?'],
    ['My relationships', 'When do I seem most at ease in my conversations?'],
    ['My plot twist', 'What might surprise me about myself?'],
  ],
};

interface ChatScreenProps {
  navigation?: NavigationProp;
  copy?: ChatCopy;
  onSendMessage?: (message: string, requestId: string) => Promise<string | CoachExchange | void>;
  history?: CoachExchange[];
  onOpenSource?: (source: CoachSource) => void;
  onConnect?: () => void;
  hasMessages?: boolean;
}

const TypingDot: React.FC<{ delay: number }> = ({ delay }) => {
  const anim = useRef(new Animated.Value(0.3)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.delay(delay),
        Animated.timing(anim, {
          toValue: 1,
          duration: 400,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(anim, {
          toValue: 0.3,
          duration: 400,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, [anim, delay]);

  return <Animated.View style={[styles.typingDot, { opacity: anim }]} />;
};

export const ChatScreen: React.FC<ChatScreenProps> = ({
  copy = askCopy,
  onSendMessage,
  history = [],
  onOpenSource,
  onConnect,
  hasMessages = true,
}) => {
  const reduceMotion = useRef(true);
  useEffect(() => {
    let active = true;
    void AccessibilityInfo.isReduceMotionEnabled().then((value) => {
      if (active) reduceMotion.current = value;
    });
    const subscription = AccessibilityInfo.addEventListener('reduceMotionChanged', (value) => {
      reduceMotion.current = value;
    });
    return () => {
      active = false;
      subscription.remove();
    };
  }, []);
  const requestRef = useRef<{ question: string; id: string } | null>(null);
  const createAnimatedMessage = useCallback(
    (msg: Omit<Message, 'fadeAnim' | 'slideAnim'>): Message => {
      if (reduceMotion.current) return msg;
      const fadeAnim = new Animated.Value(0);
      const slideAnim = new Animated.Value(msg.sender === 'user' ? 20 : -20);
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 250,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 250,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ]).start();
      return { ...msg, fadeAnim, slideAnim };
    },
    []
  );

  const [messages, setMessages] = useState<Message[]>(() =>
    history.flatMap((turn) => [
      ...(turn.kind === 'question'
        ? [
            {
              id: turn.id + '-question',
              text: turn.question,
              sender: 'user' as const,
              timestamp: new Date(turn.createdAt),
            },
          ]
        : []),
      {
        id: turn.id,
        text: turn.answer,
        sender: 'ai' as const,
        timestamp: new Date(turn.createdAt),
        sources: turn.sources,
      },
    ])
  );
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const sendingRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const [isFocused, setIsFocused] = useState(false);
  const scrollViewRef = useRef<ScrollView>(null);

  const addMessage = useCallback(
    (msg: Omit<Message, 'fadeAnim' | 'slideAnim'>) => {
      const animated = createAnimatedMessage(msg);
      setMessages((prev) => [...prev, animated]);
    },
    [createAnimatedMessage]
  );

  const sendMessage = async () => {
    if (!inputText.trim() || sendingRef.current) return;
    sendingRef.current = true;

    addMessage({
      id: Date.now().toString(),
      text: inputText,
      sender: 'user',
      timestamp: new Date(),
    });
    const currentInput = inputText;
    setInputText('');
    setIsTyping(true);

    try {
      if (!onSendMessage) throw new Error('Sign in to ask about your conversations.');
      if (requestRef.current?.question !== currentInput)
        requestRef.current = {
          question: currentInput,
          id:
            typeof crypto !== 'undefined' && crypto.randomUUID
              ? crypto.randomUUID()
              : String(Date.now()),
        };
      const response = await onSendMessage(currentInput, requestRef.current.id);
      if (!mountedRef.current) return;
      if (!response) throw new Error('The answer was empty.');
      const aiResponseText = typeof response === 'string' ? response : response.answer;
      requestRef.current = null;

      addMessage({
        id: (Date.now() + 1).toString(),
        text: aiResponseText,
        sources: typeof response === 'string' ? undefined : response.sources,
        sender: 'ai',
        timestamp: new Date(),
      });
    } catch (error) {
      if (!mountedRef.current) return;
      addMessage({
        id: (Date.now() + 1).toString(),
        text: errorMessage(error, 'Something went wrong. Tap to retry.'),
        sender: 'ai',
        timestamp: new Date(),
        isError: true,
        retryInput: currentInput,
      });
    } finally {
      sendingRef.current = false;
      if (mountedRef.current) setIsTyping(false);
    }
  };

  const handleRetry = (retryInput: string, errorMessageId: string) => {
    setMessages((prev) => prev.filter((m) => m.id !== errorMessageId));
    setInputText(retryInput);
  };

  const renderMessage = (message: Message) => {
    const isUser = message.sender === 'user';
    const animStyle = message.fadeAnim
      ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        { opacity: message.fadeAnim, transform: [{ translateX: message.slideAnim! }] }
      : {};

    return (
      <Animated.View
        key={message.id}
        style={[
          styles.messageContainer,
          isUser ? styles.userMessageContainer : styles.aiMessageContainer,
          animStyle,
        ]}
        accessibilityLabel={`${isUser ? 'You' : 'SoulThread'}: ${message.text}`}
      >
        {!isUser && (
          <View style={styles.aiAvatar} accessibilityLabel="SoulThread">
            <Text style={styles.aiAvatarText}>✦</Text>
          </View>
        )}
        <View
          style={[
            styles.messageBubble,
            isUser ? styles.userBubble : styles.aiBubble,
            message.isError && styles.errorBubble,
          ]}
        >
          <Text
            style={[
              styles.messageText,
              isUser && styles.userMessageText,
              message.isError && styles.errorMessageText,
            ]}
          >
            {message.text}
          </Text>
          {message.isError && Boolean(message.retryInput) && (
            <TouchableOpacity
              accessibilityRole="button"
              onPress={() => {
                if (message.retryInput) handleRetry(message.retryInput, message.id);
              }}
            >
              <Text style={styles.errorMessageText}>Retry</Text>
            </TouchableOpacity>
          )}
          {message.sources?.map((source, index) => {
            const label = (
              <>
                <Text style={styles.sourceLabel}>
                  [{index + 1}] {source.label} · {new Date(source.timestamp).toLocaleDateString()}
                </Text>
                {Boolean(source.excerpt) && (
                  <Text style={styles.sourceExcerpt} numberOfLines={3}>
                    “{source.excerpt}”
                  </Text>
                )}
              </>
            );
            return onOpenSource ? (
              <TouchableOpacity
                key={source.id}
                onPress={() => onOpenSource(source)}
                accessibilityRole="link"
                style={styles.source}
              >
                {label}
              </TouchableOpacity>
            ) : (
              <View key={source.id} style={styles.source}>
                {label}
              </View>
            );
          })}
          <Text style={styles.messageTime}>
            {message.timestamp.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Text>
        </View>
      </Animated.View>
    );
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      {messages.length > 0 && (
        <View style={styles.header}>
          <Text style={styles.headerTitle} accessibilityRole="header">
            {copy.title}
          </Text>
          <Text style={styles.headerSubtitle}>{copy.subtitle}</Text>
        </View>
      )}
      <ScrollView
        ref={scrollViewRef}
        style={styles.messagesContainer}
        contentContainerStyle={[styles.messagesContent, !messages.length && styles.emptyContent]}
        onContentSizeChange={() =>
          messages.length && scrollViewRef.current?.scrollToEnd({ animated: !reduceMotion.current })
        }
      >
        {!messages.length && (
          <View style={styles.welcome}>
            <View style={styles.welcomeMark}>
              <Text style={styles.welcomeSymbol}>✦</Text>
            </View>
            <Text style={styles.welcomeTitle} accessibilityRole="header">
              {copy.title}
            </Text>
            <Text style={styles.welcomeSubtitle}>{copy.welcome}</Text>
            <Text style={styles.welcomeDescription}>{copy.description}</Text>
            <View style={styles.suggestionsContainer}>
              {copy.suggestions.map(([label, question]) => (
                <TouchableOpacity
                  key={label}
                  style={styles.suggestionChip}
                  onPress={() => setInputText(question)}
                  accessibilityRole="button"
                >
                  <Text style={styles.suggestionText}>{label} ↗</Text>
                </TouchableOpacity>
              ))}
            </View>
            {!hasMessages && onConnect && (
              <TouchableOpacity
                onPress={onConnect}
                accessibilityRole="button"
                style={styles.connect}
              >
                <Text style={styles.connectText}>Connect your apps. Make it personal. →</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
        {messages.map(renderMessage)}
        {isTyping && (
          <View
            style={styles.typingIndicator}
            accessibilityLabel="SoulThread is thinking"
            accessibilityLiveRegion="polite"
          >
            <Text style={styles.headerSubtitle}>Looking for the thread</Text>
            <TypingDot delay={0} />
            <TypingDot delay={150} />
            <TypingDot delay={300} />
          </View>
        )}
      </ScrollView>

      <View style={styles.inputContainer}>
        <View style={[styles.inputWrapper, isFocused && styles.inputWrapperFocused]}>
          <TextInput
            style={styles.textInput}
            placeholder={copy.placeholder}
            placeholderTextColor={colors.textSecondary}
            value={inputText}
            onChangeText={(text) => setInputText(text.slice(0, MAX_MESSAGE_LENGTH))}
            onSubmitEditing={sendMessage}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            multiline
            blurOnSubmit={Platform.OS === 'web'}
            maxLength={MAX_MESSAGE_LENGTH}
            accessibilityLabel="Message input"
            accessibilityHint="Ask about your conversations and personality"
          />
          {inputText.length > MAX_MESSAGE_LENGTH * 0.8 && (
            <Text
              style={[
                styles.charCounter,
                inputText.length >= MAX_MESSAGE_LENGTH && styles.charCounterLimit,
              ]}
            >
              {inputText.length}/{MAX_MESSAGE_LENGTH}
            </Text>
          )}
        </View>
        <TouchableOpacity
          style={[styles.sendButton, !inputText.trim() && styles.sendButtonDisabled]}
          onPress={sendMessage}
          disabled={!inputText.trim() || isTyping}
          accessibilityLabel="Send message"
          accessibilityRole="button"
          accessibilityState={{ disabled: !inputText.trim() || isTyping }}
        >
          <Text style={styles.sendButtonText}>↑</Text>
        </TouchableOpacity>
      </View>
      <Text style={styles.footnote}>
        Reflections from your conversations. Always room for another perspective.
      </Text>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  source: { marginTop: 10, gap: 3 },
  sourceLabel: { color: colors.textSecondary, fontSize: 13 },
  sourceExcerpt: { color: colors.textSecondary, fontSize: 13, fontStyle: 'italic', opacity: 0.85 },
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    padding: spacing.lg,
    paddingTop: 24,
    width: '100%',
    maxWidth: 820,
    alignSelf: 'center',
  },
  headerTitle: {
    fontSize: fontSize.xxl,
    fontWeight: 'bold',
    color: colors.text,
  },
  headerSubtitle: {
    fontSize: fontSize.sm,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  messagesContainer: {
    flex: 1,
  },
  messagesContent: {
    padding: spacing.lg,
    width: '100%',
    maxWidth: 820,
    alignSelf: 'center',
    paddingBottom: 24,
  },
  messageContainer: {
    marginBottom: spacing.md,
  },
  userMessageContainer: {
    alignItems: 'flex-end',
  },
  aiMessageContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  aiAvatar: {
    width: 32,
    height: 32,
    borderRadius: borderRadius.full,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  aiAvatarText: {
    color: colors.text,
    fontSize: fontSize.xs,
    fontWeight: 'bold',
  },
  messageBubble: {
    maxWidth: '88%',
    flexShrink: 1,
    padding: spacing.md,
    borderRadius: borderRadius.lg,
  },
  userBubble: {
    backgroundColor: colors.primary,
    borderBottomRightRadius: borderRadius.sm,
  },
  aiBubble: {
    backgroundColor: colors.surface,
    borderTopLeftRadius: borderRadius.sm,
  },
  messageText: {
    fontSize: 15,
    color: colors.text,
    lineHeight: 24,
  },
  userMessageText: {
    color: colors.text,
  },
  messageTime: {
    fontSize: 10,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
  typingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 40,
    gap: 4,
  },
  typingDot: {
    width: 8,
    height: 8,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.textSecondary,
    marginHorizontal: 2,
  },
  emptyContent: { flexGrow: 1, justifyContent: 'center' },
  welcome: { alignItems: 'center', paddingVertical: 32 },
  welcomeMark: {
    width: 64,
    height: 64,
    borderRadius: 24,
    backgroundColor: colors.surface,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  welcomeSymbol: { fontSize: 36, color: colors.textSecondary },
  welcomeTitle: { fontSize: 52, color: colors.text, fontWeight: '500', letterSpacing: -2 },
  welcomeSubtitle: { fontSize: 20, color: colors.text, marginTop: 14, textAlign: 'center' },
  welcomeDescription: {
    fontSize: 14,
    lineHeight: 22,
    color: colors.textSecondary,
    marginTop: 10,
    textAlign: 'center',
  },
  suggestionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 10,
    marginTop: 28,
  },
  suggestionChip: {
    backgroundColor: colors.surface,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.surfaceLight,
  },
  suggestionText: { fontSize: 13, color: colors.textSecondary },
  connect: { marginTop: 28, padding: 10 },
  connectText: { color: colors.textSecondary, fontSize: 13, textAlign: 'center' },
  footnote: {
    color: colors.textSecondary,
    opacity: 0.8,
    fontSize: 11,
    textAlign: 'center',
    paddingHorizontal: 24,
    paddingBottom: 24,
  },
  inputContainer: {
    flexDirection: 'row',
    padding: spacing.lg,
    paddingBottom: 12,
    width: '100%',
    maxWidth: 820,
    alignSelf: 'center',
    alignItems: 'flex-end',
  },
  inputWrapper: {
    flex: 1,
    backgroundColor: colors.surface,
    borderRadius: borderRadius.xxl,
    marginRight: spacing.sm,
    borderWidth: 1.5,
    borderColor: colors.surfaceLight,
  },
  inputWrapperFocused: {
    borderColor: colors.primary,
  },
  textInput: {
    paddingHorizontal: spacing.md,
    paddingVertical: 16,
    minHeight: 56,
    color: colors.text,
    fontSize: fontSize.sm,
    maxHeight: 100,
  },
  charCounter: {
    fontSize: 10,
    color: colors.textMuted,
    textAlign: 'right',
    paddingRight: spacing.md,
    paddingBottom: spacing.xs,
  },
  charCounterLimit: {
    color: colors.warning,
  },
  sendButton: {
    backgroundColor: colors.primary,
    width: 48,
    height: 48,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 5,
    ...(Platform.select({
      web: { cursor: 'pointer' },
    }) as ViewStyle),
  },
  sendButtonDisabled: {
    opacity: 0.4,
  },
  sendButtonText: {
    color: colors.text,
    fontSize: fontSize.lg,
    fontWeight: '700',
  },
  errorBubble: {
    backgroundColor: `${colors.error}18`,
    borderWidth: 1,
    borderColor: `${colors.error}40`,
  },
  errorMessageText: {
    color: colors.error,
  },
});
